import { Injectable } from '@angular/core';
import { OrganizationService } from './organization.service';
import { AuthService } from './auth.service';
import * as Parse from 'parse';

export interface IThread {
    thread: Parse.Object;
    messages: Parse.Object[];
    unread: number;
}

export interface ITypedFile {
    type: string;
    file: any;
}

@Injectable()
export class ChatService {
    #propertId: string = '';
    #customerId: string = '';
    #address: string = '';

    get propertyId() {
        return this.#propertId;
    }

    set propertyId(id: string) {
        this.#propertId = id;
    }

    get customerId() {
        return this.#customerId;
    }

    set customerId(id: string) {
        this.#customerId = id;
    }

    get address(): string {
        return this.#address;
    }

    set address(address: string) {
        this.#address = address;
    }

    constructor(
        private auth: AuthService,
        private orgService: OrganizationService
    ) {}

    fetchMessageThread(propertyId: string, customerId: string) {
        const query: Parse.Query<Parse.Object> = new Parse.Query('PropertyMessageThread');

        query.equalTo('property', new Parse.Object('Property', { objectId: propertyId }));
        query.equalTo('customer', new Parse.Object('Customer', { objectId: customerId }));
        query.equalTo('organization', this.orgService.organization);

        return query.first();
    }

    createMessageThread(propertyId: string, customerId: string) {
        const object: Parse.Object = new Parse.Object('PropertyMessageThread');

        object.set('property', new Parse.Object('Property', { objectId: propertyId }));
        object.set('customer', new Parse.Object('Customer', { objectId: customerId }));
        object.set('organization', this.orgService.organization);

        return object.save();
    }

    createMessage(thread: Parse.Object, message: string, from: 'user' | 'customer', files: ITypedFile[]) {
        const object: Parse.Object = new Parse.Object('PropertyMessage');

        object.set('thread', thread);
        object.set('message', message);
        object.set('type', files.length ? 'file' : 'text');
        object.set('from', from);
        object.set('files', files);
        object.set('organization', this.orgService.organization);

        return object.save();
    }

    saveFiles(files: File[]) {
        return Promise.all(files.map((file: File) => {
            const parseFile: Parse.File = new Parse.File(file.name.replace(/[^a-zA-Z0-9]/g,'_'), file, file.type,);

            parseFile.addTag('type', file.type);

            return parseFile.save();
        }));
    }

    getMessageThreadQuery(property: Parse.Object) {
        const query: Parse.Query<Parse.Object> = new Parse.Query('PropertyMessageThread');

        query.equalTo('property', property);
        query.equalTo('agent', this.auth.currentUser);
        query.equalTo('organization', this.orgService.organization);
        query.include('customer');
        query.descending('updatedAt');

        return query;
    }

    loadMessageThreads(property: Parse.Object) {
        return this.getMessageThreadQuery(property).find();
    }

    subscribeToMessageThreads(property: Parse.Object) {
        return this.getMessageThreadQuery(property).subscribe();
    }

    getMessageQuery(thread: Parse.Object) {
        const query: Parse.Query<Parse.Object> = new Parse.Query('PropertyMessage');

        query.equalTo('thread', thread);
        query.equalTo('organization', this.orgService.organization);

        return query;
    }

    loadMessagesByThread(thread: Parse.Object) {
        return this.getMessageQuery(thread).find();
    }

    subscribeToMessages(thread: Parse.Object) {
        return this.getMessageQuery(thread).subscribe();
    }

    updateLastReadDate(thread: Parse.Object, from: 'user' | 'customer') {
        if (from === 'user') {
            thread.set('lastReadByAgent', new Date());
        } else {
            thread.set('lastReadByCustomer', new Date());
        }

        return thread.save();
    }

    updateLastSentDate(thread: Parse.Object, from: 'user' | 'customer') {
        if (from === 'user') {
            thread.set('lastSentByAgent', new Date());
        } else {
            thread.set('lastSentByCustomer', new Date());
        }

        return thread.save();
    }

    /**
     * Determine if a notification should be shown for a message
     * @param message The message object
     * @param currentUserType The type of the current user viewing the chat
     * @returns true if notification should be shown, false otherwise
     */
    shouldNotifyForMessage(
        message: Parse.Object,
        currentUserType: 'user' | 'customer'
    ): boolean {
        const messageFrom = message.get('from');
        // Notify if message is from the other party
        return messageFrom !== currentUserType;
    }
}
